import { useContext, useEffect, useRef, useState } from 'react';
import useSearchQuery from './useSearchQuery';
import { getPlaylistsAction } from 'contexts/playlists/actions';
import {
  buildIdList,
  getPageSlice,
  isSameIdList,
  playlistsInitialValues,
  toPositiveNumber,
} from 'contexts/playlists/utils';
import { IPlaylist, IPlaylistsContentHook, IPlaylistsDataCtx } from '../contexts/playlists/types';
import { deletePlaylist as deletePlaylistService } from 'services/playlists';
import { assignUsers as assignUsersService } from 'services/playlists';
import { unAssignUsers as unAssignUsersService } from 'services/playlists';
import { IAssignUsersParams } from 'services/playlists';
import { SessionContext } from 'contexts/session';
import Notification from 'components/notification';

// Hook that powers the Playlists list screen: reads filters from the URL,
// fetches data, and exposes helpers for mutations.
const usePlaylistsContent = (): IPlaylistsContentHook => {
  const [playlistsList, setPlaylistsList] = useState(playlistsInitialValues.list);
  const [fetching, setFetching] = useState(false);
  const { searchQuery, search } = useSearchQuery();
  const { account } = useContext(SessionContext);
  const business_account_id = account?.id;
  // Track if component is still mounted (avoid setting state after unmount).
  const isMountedRef = useRef(true);
  // Incrementing id to identify the latest request.
  const requestIdRef = useRef(0);
  // True while an API call is running.
  const inFlightRef = useRef(false);
  // If a new request arrives while in-flight, we remember to run it after.
  const pendingRefreshRef = useRef(false);
  // Store the last requested params while a request is in-flight (last one wins).
  const pendingRequestRef = useRef<{
    page: number | string | null;
    page_size: number | string | null;
    name__icontains: string | null;
    business_account_id?: string;
    type: string | null;
    assigned: string | null;
    created_by: string | null;
  } | null>(null);
  // Cache first-page ids by filter so we can detect backends ignoring `page`.
  const firstPageIdsRef = useRef<Record<string, string[]>>({});

  // Build a request payload from the current URL search params.
  // This keeps the API query in sync with filters/pagination in the URL.
  const handleReqData = () => {
    return {
      page: parseInt(search.get('page')) || playlistsInitialValues.filters.page,
      page_size: parseInt(search.get('page_size')) || playlistsInitialValues.filters.page_size,
      name__icontains: search.get('name') || null,
      business_account_id,
      type: search.get('type') || null,
      assigned: search.get('assigned') || null,
      created_by: search.get('createdBy') || null,
    };
  };

  // Reduce payload size and keep only fields needed by cards.
  // This prevents heavy renders when the backend includes large nested data.
  const normalizePlaylistsResults = (results: IPlaylist[]) => {
    return results.map((playlist) => {
      if (!playlist) return playlist;
      const contentCount =
        typeof playlist.content_count === 'number' ? playlist.content_count : undefined;
      const { content, ...rest } = playlist;
      return {
        ...rest,
        ...(typeof contentCount === 'number' ? { content_count: contentCount } : {}),
      };
    });
  };

  useEffect(() => {
    // Cleanup guard so we don't update state after the component is gone.
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safe setter: only update if still mounted and request is the latest.
  const setFetchingSafe = (value: boolean, requestId?: number) => {
    if (!isMountedRef.current) return;
    // Always clear the spinner, even if another request superseded this one.
    if (value === false) {
      setFetching(false);
      return;
    }
    // Ignore stale responses (not the latest request).
    if (requestId && requestId !== requestIdRef.current) return;
    setFetching(value);
  };

  // Safe setter for list state to avoid stale async updates.
  const setPlaylistsListSafe = (
    updater: (prevState: IPlaylistsDataCtx['list']) => IPlaylistsDataCtx['list'],
    requestId?: number
  ) => {
    if (!isMountedRef.current) return;
    // Ignore stale responses (not the latest request).
    if (requestId && requestId !== requestIdRef.current) return;
    setPlaylistsList(updater);
  };

  // Main fetch: handles concurrency, stale responses, and backend quirks.
  const getPlaylists = async (
    requestData: {
      page: number | string | null;
      page_size: number | string | null;
      name__icontains: string | null;
      business_account_id?: string;
      type: string | null;
      assigned: string | null;
      created_by: string | null;
    } = handleReqData()
  ) => {
    // No account id -> nothing to fetch.
    if (!business_account_id) return;
    // Prevent overlapping requests; queue the latest one for when the current finishes.
    if (inFlightRef.current) {
      // Keep only the latest request so we don't spam the API.
      pendingRequestRef.current = requestData;
      pendingRefreshRef.current = true;
      return;
    }
    const requestId = (requestIdRef.current += 1);
    inFlightRef.current = true;
    setFetchingSafe(true, requestId);
    try {
      const playlistsResponse = await getPlaylistsAction(requestData);
      if (playlistsResponse instanceof Error) {
        setPlaylistsListSafe(
          (prevState) => ({ ...prevState, error: playlistsResponse.message }),
          requestId
        );
      } else {
        // Normalize pagination inputs as numbers to avoid string math.
        const pageSize = toPositiveNumber(
          requestData.page_size,
          Number(playlistsInitialValues.filters.page_size)
        );
        const page = toPositiveNumber(
          requestData.page,
          Number(playlistsInitialValues.filters.page)
        );
        const results = Array.isArray(playlistsResponse.results) ? playlistsResponse.results : [];
        // Build a stable id list so we can compare pages reliably.
        const currentIds = buildIdList(results, (item) => item?.id ?? item?.slug ?? '');
        // Some backends return "all results" when page_size is big enough.
        // If we detect that, we'll slice client-side.
        const shouldPaginateClientSide = results.length > pageSize;
        const { slice, startIndex, endIndex } = getPageSlice(results, page, pageSize);
        let safeResults = shouldPaginateClientSide ? slice : results;

        // Key that represents the current filters (excluding page).
        const filterKey = JSON.stringify({
          page_size: pageSize,
          name__icontains: requestData.name__icontains,
          business_account_id: requestData.business_account_id,
          type: requestData.type,
          assigned: requestData.assigned,
          created_by: requestData.created_by,
        });

        // Cache first page IDs to detect backends that ignore `page` but honor `page_size`.
        if (page === 1) {
          firstPageIdsRef.current[filterKey] = currentIds;
        }

        const firstPageIds = firstPageIdsRef.current[filterKey] ?? [];
        const isSameAsFirstPage = isSameIdList(firstPageIds, currentIds);

        // If page>1 returns exactly the same items as page 1, the backend
        // is ignoring the `page` parameter. We then fetch a bigger page 1
        // and slice the correct window client-side.
        if (
          !shouldPaginateClientSide &&
          page > 1 &&
          results.length === pageSize &&
          playlistsResponse.count > pageSize &&
          isSameAsFirstPage
        ) {
          // Backend likely ignored `page`; request a bigger page 1 and slice client-side.
          // Cap fallback to avoid huge payloads.
          const fallbackPageSize = Math.min(page * pageSize, 200);
          if (fallbackPageSize > pageSize) {
            const fallbackResponse = await getPlaylistsAction({
              ...requestData,
              page: 1,
              page_size: fallbackPageSize,
            });
            if (!(fallbackResponse instanceof Error)) {
              const fallbackResults = Array.isArray(fallbackResponse.results)
                ? fallbackResponse.results
                : [];
              if (fallbackResults.length > startIndex) {
                safeResults = fallbackResults.slice(startIndex, endIndex);
              }
            }
          }
        }
        const normalizedResults = normalizePlaylistsResults(safeResults);
        setPlaylistsListSafe(
          (prevState) => ({
            ...prevState,
            count: playlistsResponse.count,
            // list.error is a string in context, so use a string "false".
            error: 'false',
            items: normalizedResults,
          }),
          requestId
        );
      }
    } catch (error) {
      // Keep error visible in UI for debugging and support.
      const errorMessage =
        (error && typeof error === 'object' && 'message' in error && (error as any).message) ||
        'Unknown error';
      setPlaylistsListSafe(
        (prevState) => ({
          ...prevState,
          error: `Unable to load playlists: ${errorMessage}`,
        }),
        requestId
      );
    } finally {
      // Finish request and run any queued one.
      inFlightRef.current = false;
      setFetchingSafe(false, requestId);
      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        const nextRequest = pendingRequestRef.current;
        pendingRequestRef.current = null;
        getPlaylists(nextRequest ?? handleReqData());
      }
    }
  };

  const handleDeletePlaylist = async (playlist: IPlaylist) => {
    let response;
    setFetching(true);
    try {
      response = await deletePlaylistService({
        id: playlist.id,
        business_account_id: business_account_id,
      });
      return response;
    } catch (error) {
      return new Error(error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    // Refetch whenever the URL search params change (pagination/filters).
    getPlaylists(handleReqData());
  }, [searchQuery]);

  const updatePlaylistsList = () => {
    // Manual refresh helper (used after mutations).
    getPlaylists();
  };

  const handleAssignUser = async (playlist: IPlaylist) => {
    setFetching(true);
    try {
      if (!playlist.created_for) {
        Notification({ text: 'There has been a problem assigning user', type: 'error' });
        throw new Error('No user to assign');
      }
      const reqData: IAssignUsersParams = {
        id: playlist.id,
        user_ids: [playlist.created_for?.id],
        business_account_id: business_account_id,
      };

      const response = await assignUsersService(reqData);
      if (response.status === 200) {
        Notification({
          text: `You've assigned ${playlist.name} playlist`,
          type: 'success',
        });
        updatePlaylistsList();
      } else {
        Notification({ text: 'There has been a problem assigning user', type: 'error' });
      }
    } catch (error) {
      if (error?.response?.data?.details) {
        Notification({ text: error.response.data.details, type: 'error' });
      }
    }
  };

  const handleUnassignUser = async (playlist: IPlaylist) => {
    setFetching(true);
    try {
      if (!playlist.created_for) {
        Notification({ text: 'There has been a problem unassigning user', type: 'error' });
        throw new Error('No user to unassign');
      }
      const reqData: IAssignUsersParams = {
        id: playlist.id,
        user_ids: [playlist.created_for?.id],
        business_account_id: business_account_id,
      };
      const response = await unAssignUsersService(reqData);
      if (response.status === 204) {
        Notification({
          text: `You've unassigned ${playlist.name} playlist`,
          type: 'success',
        });
        updatePlaylistsList();
      } else {
        Notification({ text: 'There has been a problem unassigning user', type: 'error' });
      }
    } catch (error) {
      if (error?.response?.data?.details) {
        Notification({ text: error.response.data.details, type: 'error' });
      }
    }
  };

  return {
    fetching: fetching,
    playlists: playlistsList,
    error: false,
    updatePlaylistsList: updatePlaylistsList,
    deletePlaylist: handleDeletePlaylist,
    refreshPlaylists: getPlaylists,
    onAssignUser: handleAssignUser,
    onUnassignUser: handleUnassignUser,
  };
};

export default usePlaylistsContent;
